import { useCallback, useMemo } from "react";

import { shuffleWithSeed } from "@vericus/cadmus-common";
import { Alert, Checkbox, RadioGroup, Text } from "@vericus/cadmus-ui";

import { useAppDispatch, useAppSelector } from "@/data/hooks";
import { selectAnswerSnapshot, setLocalSnapshot } from "@/features/authority";
import { Snapshot } from "@/stores/snapshot";

import * as styles from "./answer-form.css";

interface Props {
  /** Field identifier */
  identifier: string;
  /** Answer Block ID */
  answerBlockId: string;
  /** Choices for the multiple choice question. */
  choices: Array<{ content: string; identifier: string }>;
  /** Make the choices readonly */
  isReadonly?: boolean;
  /** Whether shuffle options */
  shuffle?: boolean;
  /** Maximum number of choices allowed */
  maxChoices: number;
}

/**
 * Answer response form section to respond to a multiple choice question.
 */
export function MultiResponseForm(props: Props) {
  const {
    identifier,
    answerBlockId,
    isReadonly = false,
    choices,
    shuffle,
    maxChoices,
  } = props;

  const dispatch = useAppDispatch();
  const snapshot = useAppSelector((state) =>
    selectAnswerSnapshot(state, answerBlockId)
  );
  const version = snapshot?.version ?? 0;

  const answerChoiceIds = useMemo(
    () => getAnswerChoiceIds(snapshot, identifier),
    [snapshot, identifier]
  );

  const choicesToDisplay = useMemo(() => {
    if (!shuffle) return choices;
    return shuffleWithSeed(choices, answerBlockId);
  }, [shuffle, choices, answerBlockId]);

  // Callback to set choice in the local snapshot
  const setChoice = useCallback(
    (choiceId: string) => {
      const newVersion = version + 1;
      let modifiedAnswerChoiceIds = [];

      if (maxChoices > 1) {
        modifiedAnswerChoiceIds = [...answerChoiceIds];
        if (!modifiedAnswerChoiceIds.includes(choiceId)) {
          modifiedAnswerChoiceIds.push(choiceId);
        } else {
          modifiedAnswerChoiceIds = modifiedAnswerChoiceIds.filter(
            (id) => id !== choiceId
          );
        }
      } else {
        modifiedAnswerChoiceIds = [choiceId];
      }

      const snapshot: Snapshot = {
        version: newVersion,
        answerChoiceIds: modifiedAnswerChoiceIds,
        answerFields: {
          [identifier]: modifiedAnswerChoiceIds,
        },
      };
      dispatch(
        setLocalSnapshot({
          answerBlockId,
          snapshot,
        })
      );
    },
    [identifier, dispatch, version, answerBlockId, answerChoiceIds, maxChoices]
  );

  // Handler for checkbox changes (multiple choice)
  const handleCheckboxChange = useCallback(
    (event: { target: { checked: boolean; value: string } }) => {
      setChoice(event.target.value);
    },
    [setChoice]
  );

  // Handler for radio group value change (single choice)
  const handleRadioChange = useCallback(
    (value: string) => {
      setChoice(value);
    },
    [setChoice]
  );

  if (maxChoices && maxChoices > 1) {
    // Multiple choices - use Checkbox
    return (
      <div className={styles.optionResponseForm}>
        {choicesToDisplay.map((choice) => (
          <Checkbox
            key={choice.identifier}
            className={
              styles.option[
                answerChoiceIds.includes(choice.identifier)
                  ? "selected"
                  : "default"
              ]
            }
            value={choice.identifier}
            checked={answerChoiceIds.includes(choice.identifier)}
            onChange={handleCheckboxChange}
            disabled={isReadonly}
            colorVariant="primary"
          >
            <Text kind="bodyMd" as="div">
              {choice.content}
            </Text>
          </Checkbox>
        ))}

        <Alert
          title="This question has multiple correct answers"
          size="sm"
          severity="info"
        />
      </div>
    );
  }

  // Single choice - use RadioGroup
  return (
    <div className={styles.optionResponseForm}>
      <RadioGroup.Root
        value={answerChoiceIds[0] ?? ""}
        onValueChange={handleRadioChange}
        disabled={isReadonly}
        className={styles.radioGroup}
      >
        {choicesToDisplay.map((choice) => (
          <RadioGroup.Card
            key={choice.identifier}
            value={choice.identifier}
            className={styles.radioCard}
          >
            <Text kind="bodyMd" as="div">
              {choice.content}
            </Text>
          </RadioGroup.Card>
        ))}
      </RadioGroup.Root>
    </div>
  );
}

function getAnswerChoiceIds(
  snapshot: Snapshot | undefined,
  identifier: string
): string[] {
  if (snapshot?.answerFields) {
    return snapshot.answerFields[identifier] ?? [];
  }
  return snapshot?.answerChoiceIds ?? [];
}
